import { useMemo, useState, useId } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  useGiveKudos,
  useKudosCategoryTags,
  useKudosRecipients,
} from '../../hooks/useKudos';
import { useAuth } from '../../services/authContext';
import type { ApiClientError } from '../../services/api/client';

interface GiveKudosModalProps {
  /**
   * Pre-filled recipient userId. When provided, the recipient field is read-only
   * (per-profile flow). When omitted, the modal renders a search-and-pick directory
   * (sidebar-shortcut flow — Option C, replaces unreachable /profile/:id).
   */
  recipientUserId?: number;
  /** Display name shown read-only when recipientUserId is provided. */
  recipientName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const MAX_MESSAGE = 280;

export function GiveKudosModal({
  recipientUserId,
  recipientName,
  onClose,
  onSuccess,
}: GiveKudosModalProps) {
  const { user } = useAuth();
  const titleId = useId();
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  // Picker mode (recipientUserId undefined): user picks a recipient from the
  // directory; the search/select state lives locally and only the resolved
  // pickedRecipient.id is sent on submit.
  const pickerMode = recipientUserId === undefined;
  const [pickedRecipientId, setPickedRecipientId] = useState<number | null>(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const recipients = useKudosRecipients();

  const { data: tags = [], isLoading: tagsLoading } = useKudosCategoryTags();
  const giveKudos = useGiveKudos();

  const effectiveRecipientId = recipientUserId ?? pickedRecipientId ?? null;

  // Defense-in-depth: block submit if somehow the recipient is the current user.
  const isSelf =
    effectiveRecipientId !== null && user?.id === effectiveRecipientId;

  const filteredRecipients = useMemo(() => {
    const list = recipients.data ?? [];
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
  }, [recipients.data, pickerQuery]);

  const pickedRecipientName = useMemo(() => {
    if (!pickedRecipientId) return null;
    return recipients.data?.find((r) => r.id === pickedRecipientId)?.name ?? null;
  }, [pickedRecipientId, recipients.data]);

  const remaining = MAX_MESSAGE - message.length;
  const isOverLimit = remaining < 0;
  const canSubmit =
    !isSelf &&
    effectiveRecipientId !== null &&
    selectedTagId !== null &&
    message.trim().length > 0 &&
    !isOverLimit &&
    !giveKudos.isPending;

  const handleSubmit = () => {
    if (!canSubmit || selectedTagId === null || effectiveRecipientId === null) return;
    setServerError(null);
    giveKudos.mutate(
      { toUserId: effectiveRecipientId, categoryTagId: selectedTagId, message: message.trim() },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
        onError: (err) => {
          const apiErr = err as ApiClientError;
          setServerError(apiErr.message ?? 'Something went wrong. Please try again.');
        },
      },
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Give kudos"
      size="lg"
      ariaLabelledBy={titleId}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={giveKudos.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={giveKudos.isPending}>
            Send kudos
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Recipient */}
        <div>
          <label
            htmlFor={pickerMode ? 'kudos-recipient-search' : undefined}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            To
          </label>
          {pickerMode ? (
            pickedRecipientId !== null ? (
              <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
                <span className="text-gray-800">
                  {pickedRecipientName ?? `User #${pickedRecipientId}`}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPickedRecipientId(null);
                    setPickerQuery('');
                  }}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <input
                  id="kudos-recipient-search"
                  type="text"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search by name or email…"
                  autoComplete="off"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <div
                  className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200"
                  role="listbox"
                  aria-label="Kudos recipients"
                >
                  {recipients.isLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-400">Loading directory…</div>
                  ) : recipients.isError ? (
                    <div className="px-3 py-2 text-sm text-red-600">
                      Could not load the user directory. Try again later.
                    </div>
                  ) : filteredRecipients.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-400">No users found.</div>
                  ) : (
                    filteredRecipients.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => setPickedRecipientId(r.id)}
                        className="flex w-full flex-col items-start gap-0.5 border-b border-gray-100 px-3 py-2 text-left text-sm hover:bg-blue-50 last:border-b-0"
                      >
                        <span className="font-medium text-gray-800">{r.name}</span>
                        <span className="text-xs text-gray-500">{r.email}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
              {recipientName ?? `User #${recipientUserId}`}
            </div>
          )}
          {isSelf && (
            <p className="mt-1 text-xs text-red-600">You cannot give kudos to yourself.</p>
          )}
        </div>

        {/* Category tag picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          {tagsLoading ? (
            <div className="h-8 w-48 animate-pulse rounded bg-gray-100" />
          ) : (
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Category tag">
              {tags.map((tag) => (
                <label key={tag.id} className="cursor-pointer">
                  <input
                    type="radio"
                    name="category-tag"
                    value={tag.id}
                    checked={selectedTagId === tag.id}
                    onChange={() => setSelectedTagId(tag.id)}
                    className="sr-only"
                  />
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium border transition-colors ${
                      selectedTagId === tag.id
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    {tag.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Message */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="kudos-message" className="block text-sm font-medium text-gray-700">
              Message
            </label>
            <span
              className={`text-xs ${isOverLimit ? 'text-red-600 font-semibold' : 'text-gray-400'}`}
              aria-live="polite"
            >
              {remaining} / {MAX_MESSAGE}
            </span>
          </div>
          <textarea
            id="kudos-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={280}
            rows={4}
            placeholder="Write a short message of appreciation…"
            className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 ${
              isOverLimit
                ? 'border-red-400 focus:ring-red-300'
                : 'border-gray-300 focus:ring-blue-300'
            }`}
          />
        </div>

        {/* Server error */}
        {serverError && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          >
            {serverError}
          </div>
        )}
      </div>
    </Modal>
  );
}
